import React from "react";
import { useThunkDispatch } from "../../store";
import { showDetailedError } from "../../store/notifications/actions";

type UseLazyDataOptions<TData> = {
    /** The data-fetching callback.  May return the data directly or a Promise resolving to it.  If undefined, the hook is inert. */
    fetcher?: (...args: any[]) => TData | Promise<TData>;
    /** The user-facing error message shown (via notification) when the fetcher throws or rejects. */
    errorMessage: string;
};

type UseLazyDataResult<TData> = {
    data: TData | undefined;
    isLoading: boolean;
    /** True once the first fetch has settled - distinguishes "empty result" from "not fetched yet". */
    hasLoaded: boolean;
    /** Invokes the fetcher with the given arguments.  Referentially stable. */
    load: (...args: any[]) => Promise<void>;
    /** Overwrites the current data - used to apply local edits to fetched data. */
    setData: React.Dispatch<React.SetStateAction<TData>>;
};

/**
 * Manages the lifecycle of lazily-fetched component data: invokes a caller-supplied fetcher (synchronous or
 * Promise-returning), tracks loading state, discards stale responses when requests overlap (latest wins), and
 * surfaces failures as error notifications.
 *
 * The fetcher is kept in a ref so that `load` remains referentially stable even though template-supplied arrow
 * props are recreated on every render - callers can safely omit the fetcher from effect dependencies.
 */
export const useLazyData = <TData,>({ fetcher, errorMessage }: UseLazyDataOptions<TData>): UseLazyDataResult<TData> => {
    const [data, setData] = React.useState<TData | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const dispatch = useThunkDispatch();

    const fetcherRef = React.useRef(fetcher);
    fetcherRef.current = fetcher;

    const requestIdRef = React.useRef(0);
    const isMountedRef = React.useRef(true);

    React.useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = React.useCallback(async (...args: any[]) => {
        if (!fetcherRef.current) return;

        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        try {
            const result = await Promise.resolve(fetcherRef.current(...args));

            // A newer request has been issued (or we've unmounted) - discard this response...
            if (requestId !== requestIdRef.current || !isMountedRef.current) return;

            setData(result);
        }
        catch (error: any) {
            if (requestId !== requestIdRef.current || !isMountedRef.current) return;

            setData(undefined);
            dispatch(showDetailedError(errorMessage, String(error?.message ?? error)));
        }
        finally {
            if (requestId === requestIdRef.current && isMountedRef.current) {
                setIsLoading(false);
                setHasLoaded(true);
            }
        }
    }, [dispatch, errorMessage]);

    return { data, isLoading, hasLoaded, load, setData };
};
